import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Anchor, Compass } from "lucide-react";
import api from "@/lib/api";
import { Container, Kicker, SectionHeading, GoldRule, GoldButton, OutlineButton, PrimaryButton, AdvertBadge } from "@/components/shared";
import ListingCard from "@/components/ListingCard";
import { PLANS } from "@/data/plans";

const HERO_STUMP = "https://images.pexels.com/photos/8755712/pexels-photo-8755712.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const MASS_IMG = "https://images.unsplash.com/photo-1717174704421-0ce3dc8a7225?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Home() {
  const [listings, setListings] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [events, setEvents] = useState([]);
  const [articles, setArticles] = useState([]);
  const [crossing, setCrossing] = useState(null);

  useEffect(() => {
    api.get("/listings").then((r) => setListings(r.data));
    api.get("/listings/featured").then((r) => {
      setFeatured(r.data);
      const c = r.data.find((l) => l.featured) || r.data[0];
      setCrossing(c || null);
    });
    api.get("/events").then((r) => setEvents(r.data.slice(0, 3)));
    api.get("/articles").then((r) => setArticles(r.data.slice(0, 2)));
  }, []);

  const linc = listings.filter((l) => l.shore === "lincolnshire" || l.shore === "both").slice(0, 3);
  const mass = listings.filter((l) => l.shore === "massachusetts" || l.shore === "both").slice(0, 3);

  return (
    <div>
      {/* Kicker + Hero */}
      <section className="relative overflow-hidden border-b border-[#D6D3CB]">
        <Container className="py-16 sm:py-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-6 rise-in">
              <Kicker className="mb-6">An independent crossing · madeinboston.co.uk</Kicker>
              <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#003366] leading-[1.05]">
                Two harbours.
                <br /> One name.
              </h1>
              <p className="font-playfair italic text-xl sm:text-2xl text-[#1E1E2F] mt-6">
                From the Stump to the Common.
              </p>
              <p className="font-sans text-base text-[#505A68] leading-relaxed mt-5 max-w-md">
                An independent business-to-business crossing between Boston, Lincolnshire — the
                mother town — and Boston, Massachusetts, the daughter city. A trade directory, a
                marketplace, and a members' forum, built on four centuries of shared history.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <GoldButton as="link" to="/made" data-testid="hero-explore-made">
                  Explore the Made directory <ArrowRight size={16} className="ml-2" />
                </GoldButton>
                <OutlineButton as="link" to="/story/1630" data-testid="hero-read-1630" className="px-6 py-3">
                  Read 1630
                </OutlineButton>
              </div>
            </div>

            <div className="lg:col-span-6 rise-in" style={{ animationDelay: "0.12s" }}>
              <div className="relative grid grid-cols-5 gap-3">
                <div className="col-span-3 relative">
                  <img src={HERO_STUMP} alt="St Botolph's, the Stump, Boston, Lincolnshire" className="w-full h-[380px] object-cover border border-[#D6D3CB]" />
                  <span className="absolute bottom-3 left-3 bg-[#1E1E2F] text-[#FFFAF3] text-xs font-sans px-3 py-1.5 tracking-wide">
                    Lincolnshire · Mother town
                  </span>
                </div>
                <div className="col-span-2 relative mt-10">
                  <img src={MASS_IMG} alt="Boston Harbour, Massachusetts" className="w-full h-[340px] object-cover border border-[#D6D3CB]" />
                  <span className="absolute bottom-3 left-3 bg-[#003366] text-[#FFFAF3] text-xs font-sans px-3 py-1.5 tracking-wide">
                    Massachusetts · Daughter city
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Two place cards */}
      <section className="py-16 sm:py-24">
        <Container>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <PlaceCard
              to="/lincolnshire"
              testId="place-card-lincolnshire"
              tag="The mother town"
              title="Boston, Lincolnshire"
              body="A Fenland market town on the River Witham, gathered beneath the Stump — the tower of St Botolph's, seen for miles across the marsh."
              img={HERO_STUMP}
              accent="#003366"
            />
            <PlaceCard
              to="/massachusetts"
              testId="place-card-massachusetts"
              tag="The daughter city"
              title="Boston, Massachusetts"
              body="Named on 7 September 1630, grown from a small peninsula settlement into a great harbour city — and still carrying the old name."
              img={MASS_IMG}
              accent="#1E1E2F"
            />
          </div>
        </Container>
      </section>

      {/* Crossing of the week */}
      {crossing && (
        <section className="pb-16 sm:pb-24">
          <Container>
            <div className="flex items-center justify-between mb-6">
              <Kicker>Crossing of the week</Kicker>
              <AdvertBadge label="Paid listing" />
            </div>
            <Link
              to={`/made/${crossing.slug}`}
              data-testid="crossing-of-the-week"
              className="group grid md:grid-cols-2 gap-0 bg-[#FFFAF3] border-2 border-[#CDA434] overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden">
                <img src={crossing.image_url} alt={crossing.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-8 sm:p-12 flex flex-col justify-center">
                <span className="font-sans text-xs uppercase tracking-[0.15em] text-[#505A68] mb-3">
                  {crossing.category} · {crossing.town}
                </span>
                <h3 className="font-serif font-semibold text-2xl sm:text-3xl text-[#1E1E2F] group-hover:text-[#003366] transition-colors">
                  {crossing.name}
                </h3>
                <p className="font-playfair italic text-lg text-[#7a611c] mt-3">{crossing.short_pitch}</p>
                <p className="font-sans text-sm text-[#505A68] leading-relaxed mt-4 line-clamp-3">
                  {crossing.origin_paragraph}
                </p>
                <span className="mt-6 inline-flex items-center gap-1 font-sans text-sm font-semibold text-[#003366]">
                  Read the full listing <ArrowUpRight size={16} />
                </span>
              </div>
            </Link>
          </Container>
        </section>
      )}

      {/* Why the name */}
      <section className="py-16 sm:py-20 bg-[#1E1E2F] text-[#FFFAF3]">
        <Container>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Kicker className="mb-4">Why the name</Kicker>
              <blockquote className="font-playfair italic text-2xl sm:text-3xl leading-relaxed text-[#FFFAF3] border-l-2 border-[#CDA434] pl-6">
                “On 7 September 1630 the settlement then called Trimountaine was renamed Boston,
                after Boston in Lincolnshire — the town so many of its Puritan settlers had left
                behind.”
              </blockquote>
              <p className="font-sans text-sm text-[#FFFAF3]/70 leading-relaxed mt-6 max-w-xl">
                John Cotton preached at St Botolph's from 1612. When the Winthrop fleet sailed for
                Massachusetts Bay in 1630, the name crossed the ocean with them. 2030 marks four
                hundred years.
              </p>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <GoldButton as="link" to="/story/1630" data-testid="why-name-read-more">
                Read the 1630 story <ArrowRight size={16} className="ml-2" />
              </GoldButton>
            </div>
          </div>
        </Container>
      </section>

      {/* Made on both shores */}
      <section className="py-16 sm:py-24">
        <Container>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <SectionHeading
              kicker="Listed by sector"
              title="Trading on both shores"
              intro="A handful of the firms trading across the crossing. Company profiles are free and organised by sector; browse the full directory, or visit the Trade floor for their products and services."
            />
            <OutlineButton as="link" to="/made" className="whitespace-nowrap">
              All makers <ArrowRight size={15} className="ml-2" />
            </OutlineButton>
          </div>

          <div className="mb-4 flex items-center gap-3">
            <span className="font-serif text-lg text-[#003366]">Lincolnshire</span>
            <div className="flex-1 h-px bg-[#D6D3CB]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12">
            {linc.map((l) => (
              <ListingCard key={l.id} listing={l} />
            ))}
          </div>

          <div className="mb-4 flex items-center gap-3">
            <span className="font-serif text-lg text-[#1E1E2F]">Massachusetts</span>
            <div className="flex-1 h-px bg-[#D6D3CB]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {mass.map((l) => (
              <ListingCard key={l.id} listing={l} />
            ))}
          </div>
        </Container>
      </section>

      {/* 2030 ledger */}
      <section className="py-16 sm:py-24 bg-[#FFFAF3] border-y border-[#D6D3CB]">
        <Container>
          <SectionHeading
            kicker="The 400th year"
            title="The 2030 ledger"
            intro="A running record of gatherings marking four hundred years since the naming — on both shores."
            className="mb-10"
          />
          <div className="grid md:grid-cols-3 gap-6">
            {events.map((e) => (
              <div key={e.id} className="bg-[#F8F1E7] border border-[#D6D3CB] p-6 hover:border-[#CDA434] transition-colors">
                <p className="font-playfair italic text-base text-[#CDA434] mb-3">
                  {new Date(e.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                </p>
                <h3 className="font-serif text-lg text-[#1E1E2F] mb-2">{e.title}</h3>
                <p className="font-sans text-xs uppercase tracking-wide text-[#505A68] mb-3">{e.place}</p>
                <p className="font-sans text-sm text-[#505A68] leading-relaxed line-clamp-3">{e.summary}</p>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <OutlineButton as="link" to="/2030">See the full ledger <ArrowRight size={15} className="ml-2" /></OutlineButton>
          </div>
        </Container>
      </section>

      {/* Journal teasers */}
      <section className="py-16 sm:py-24">
        <Container>
          <SectionHeading kicker="From the journal" title="Dispatches from the crossing" className="mb-10" />
          <div className="grid md:grid-cols-2 gap-8">
            {articles.map((a) => (
              <Link
                key={a.id}
                to={`/journal/${a.slug}`}
                data-testid={`journal-teaser-${a.slug}`}
                className="group bg-[#FFFAF3] border border-[#D6D3CB] p-8 hover:border-[#CDA434] transition-colors"
              >
                <p className="font-playfair italic text-sm text-[#CDA434] mb-3">
                  {new Date(a.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                </p>
                <h3 className="font-serif text-xl sm:text-2xl text-[#1E1E2F] group-hover:text-[#003366] transition-colors leading-snug">
                  {a.title}
                </h3>
                <p className="font-sans text-sm text-[#505A68] leading-relaxed mt-3">{a.deck}</p>
                <span className="mt-5 inline-flex items-center gap-1 font-sans text-sm font-semibold text-[#003366]">
                  Read the piece <ArrowUpRight size={15} />
                </span>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* Membership strip */}
      <section className="py-16 sm:py-24 bg-[#1E1E2F] text-[#FFFAF3]">
        <Container>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div>
              <Kicker className="mb-3">Membership</Kicker>
              <h2 className="font-serif font-semibold text-2xl sm:text-3xl lg:text-4xl text-[#FFFAF3]">
                Cross as a guest. Trade as a member.
              </h2>
            </div>
            <GoldButton as="link" to="/join" className="whitespace-nowrap">Compare plans</GoldButton>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PLANS.map((p) => (
              <div key={p.key} className="border border-[#FFFAF3]/15 bg-[#161622] p-6 flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                  {p.key === "harbour" ? <Anchor size={16} className="text-[#CDA434]" /> : <Compass size={16} className="text-[#CDA434]" />}
                  <h3 className="font-serif text-lg text-[#FFFAF3]">{p.name}</h3>
                </div>
                <p className="font-sans text-sm text-[#CDA434] font-semibold">{p.price_month} {p.price_year}</p>
                <p className="font-sans text-xs text-[#FFFAF3]/60 leading-relaxed mt-3 flex-1">{p.summary}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>
    </div>
  );
}

function PlaceCard({ to, testId, tag, title, body, img, accent }) {
  return (
    <Link to={to} data-testid={testId} className="group relative overflow-hidden border border-[#D6D3CB] bg-[#FFFAF3] hover:border-[#CDA434] transition-colors">
      <div className="relative h-64 overflow-hidden">
        <img src={img} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${accent}CC, transparent 60%)` }} />
        <span className="absolute top-4 left-4 bg-[#FFFAF3] text-[#1E1E2F] text-xs font-sans uppercase tracking-widest px-3 py-1.5">
          {tag}
        </span>
      </div>
      <div className="p-8">
        <h3 className="font-serif font-semibold text-2xl text-[#1E1E2F] group-hover:text-[#003366] transition-colors">{title}</h3>
        <p className="font-sans text-sm text-[#505A68] leading-relaxed mt-3">{body}</p>
        <span className="mt-5 inline-flex items-center gap-1 font-sans text-sm font-semibold text-[#003366]">
          Explore this shore <ArrowRight size={15} />
        </span>
      </div>
    </Link>
  );
}
